import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AppState } from '../store/app-state.reducer';
import { setSelectedApplication } from '../store/app-state.actions';
import { selectSelectedApplication } from '../store/app-state.selectors';
import { LOCAL_STORAGE_CONSTANTS } from './models/local-storage.constants';

/**
 * Central place for reading, persisting, and committing the user's selected
 * application.
 */
@Injectable({
  providedIn: 'root'
})
export class AppSelectionService {

  constructor(private store: Store<AppState>, private router: Router) {}

  getPersistedAppId(): number | null {
    const raw = localStorage.getItem(LOCAL_STORAGE_CONSTANTS.SELECTED_APPLICATION_ID);
    if (!raw) {
      return null;
    }
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }

  persistAppId(applicationId: number): void {
    localStorage.setItem(LOCAL_STORAGE_CONSTANTS.SELECTED_APPLICATION_ID, String(applicationId));
  }

  clearPersistedApp(): void {
    localStorage.removeItem(LOCAL_STORAGE_CONSTANTS.SELECTED_APPLICATION_ID);
  }

  /**
   * Commit an app choice and enter the dashboard.
   *
   * Switching away from an app already in use hard-reloads: a full document load
   * wipes all in-memory state (NgRx BoardState, the request cache, component
   * state), guaranteeing no data from the previous app bleeds into the new one,
   * while MSAL keeps the user signed in. Selecting for the first time in a
   * session has nothing to wipe, so it routes normally and skips the reload.
   *
   * Either way the URL is replaced rather than pushed, so Back never lands on
   * the transient /loading or /select-app pages.
   */
  selectApplication(applicationId: number): void {
    this.persistAppId(applicationId);

    this.store.pipe(
      select(selectSelectedApplication),
      take(1)
    ).subscribe(previousId => {
      const isSwitchingApps = previousId !== null && previousId !== applicationId;

      this.store.dispatch(setSelectedApplication({ applicationId }));

      if (isSwitchingApps) {
        window.location.replace('/dashboard');
      } else {
        this.router.navigate(['/dashboard'], { replaceUrl: true });
      }
    });
  }
}
